import { UseQueryResult } from "@tanstack/react-query";
import dayjs, { Dayjs } from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createSearchParams, useSearchParams } from "react-router-dom";
import { QUERY_PARAMS_KEYS } from "consts";
import { HookWithQueryParams } from "types";
import { getParsedQueryParams } from "utils";

export const useYearMonthPeriodFilter = ({
  updateExternalStates,
  getQueryParamsWithExternalChanges,
  withQueryParams = true,
  initialValue,
  isYearly = false,
}: HookWithQueryParams & {
  useYears?: () => UseQueryResult<number[], unknown>;
  initialValue?: number;
} = {}) => {
  const availableYears: any[] = useMemo(
    () => [2020, 2021, 2022, 2023, 2024, 2025, 2026, 2027, 2028, 2029, 2030],
    [],
  );
  const [period, setPeriod] = useState<Dayjs>(dayjs().utc());
  const [searchParams, setSearchParams] = useSearchParams();

  const isInitialQueryReadRef = useRef<boolean>(true);

  useEffect(() => {
    if (availableYears && isInitialQueryReadRef.current && withQueryParams) {
      isInitialQueryReadRef.current = false;
    }
  }, [initialValue, availableYears, searchParams, withQueryParams]);

  const onPeriodAccept = useCallback(
    (y: Dayjs | null) => {
      if (updateExternalStates) {
        updateExternalStates();
      }

      setPeriod(y || dayjs().utc());

      if (withQueryParams) {
        const queryParams = getParsedQueryParams(searchParams);

        if (y?.year() !== undefined && y?.month !== undefined) {
          queryParams[QUERY_PARAMS_KEYS.YEAR] = String(y.year());
          queryParams[QUERY_PARAMS_KEYS.MONTH] = isYearly
            ? "1"
            : String(y.month() + 1);
        }

        setSearchParams(
          createSearchParams({
            ...(getQueryParamsWithExternalChanges
              ? getQueryParamsWithExternalChanges(queryParams)
              : queryParams),
          }),
        );
      }
    },
    [
      updateExternalStates,
      withQueryParams,
      searchParams,
      setSearchParams,
      getQueryParamsWithExternalChanges,
      isYearly,
    ],
  );

  return {
    startDate: isYearly
      ? period?.startOf("year").format("YYYY-MM-DD")
      : `${period.format("YYYY-MM")}-01`,
    endDate: isYearly
      ? period?.endOf("year").format("YYYY-MM-DD")
      : `${period.format("YYYY-MM")}-${period?.daysInMonth()}`,
    period,
    periodStr: isYearly ? period.format("YYYY") : period.format("MMM-YY"),
    onPeriodChange: () => null,
    onPeriodAccept,
    availableYears,
  };
};